import React, {useCallback, useEffect, useRef, useState} from "react"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro"

import Constants from "../../constants"
import HttpManager from "../../services/HttpManager"
import {getErrorMessage} from "../../common"

export interface InvoiceNotesProps
{
	sessionToken : string | null
	campusId     : number
	studentId    : number
	invoiceId    : number
	canWrite     : boolean
}

interface Nota
{
	id            : number
	note          : string
	original      : boolean
	createdBy     : string | null
	createdAt     : string | null
	edited        : boolean
	editable      : boolean
}

const fechaYHora = (valor : string | null) : string =>
{
	if(!valor)
	{
		return "-"
	}

	const fecha = new Date(valor)

	if(isNaN(fecha.getTime()))
	{
		return "-"
	}

	return fecha.toLocaleString
	(
		"es",
		{day : "2-digit", month : "2-digit", year : "numeric", hour : "2-digit", minute : "2-digit"}
	)
}

/**
 * Notas de la factura en bitácora (Esteban, 2026-09-17): la nota con la que se emitió no se edita nunca;
 * lo demás se agrega como notas nuevas firmadas con autor y fecha. Mismo patrón que las observaciones del alumno.
 */
const InvoiceNotes : React.FC<InvoiceNotesProps> = ({sessionToken, campusId, studentId, invoiceId, canWrite}) =>
{
	const http = HttpManager.getInstance()
	const [notas, setNotas] = useState<Nota[] | null>(null)
	const [nueva, setNueva] = useState<string>("")
	const [enEdicion, setEnEdicion] = useState<number | null>(null)
	const [textoEdicion, setTextoEdicion] = useState<string>("")
	const [ocupado, setOcupado] = useState<boolean>(false)
	const [faltaTexto, setFaltaTexto] = useState<boolean>(false)
	const cajaTexto = useRef<HTMLTextAreaElement>(null)
	const [error, setError] = useState<string | null>(null)

	const cargar = useCallback
	(
		()=>
		{
			setError(null)
			http.invoiceService.getInvoiceNotes(sessionToken, campusId, studentId, invoiceId)
				.then((respuesta : any) => setNotas(respuesta.data.notes || []))
				.catch((e : any) => {setNotas([]); setError(getErrorMessage(e))})
		},
		[sessionToken, campusId, studentId, invoiceId]
	)

	useEffect(() => {cargar()}, [cargar])

	const agregar = async ()=>
	{
		if(nueva.trim() === "")
		{
			setFaltaTexto(true)
			cajaTexto.current?.focus()

			return
		}

		setFaltaTexto(false)
		setOcupado(true)
		setError(null)

		try
		{
			await http.invoiceService.addInvoiceNote(sessionToken, campusId, studentId, invoiceId, nueva.trim())
			setNueva("")
			cargar()
		}
		catch(e : any)
		{
			setError(getErrorMessage(e))
		}
		finally
		{
			setOcupado(false)
		}
	}

	const guardarEdicion = async (nota : Nota)=>
	{
		if(textoEdicion.trim() === "")
		{
			return
		}

		setOcupado(true)
		setError(null)

		try
		{
			await http.invoiceService.updateInvoiceNote(sessionToken, campusId, studentId, invoiceId, nota.id, textoEdicion.trim())
			setEnEdicion(null)
			setTextoEdicion("")
			cargar()
		}
		catch(e : any)
		{
			setError(getErrorMessage(e))
		}
		finally
		{
			setOcupado(false)
		}
	}

	return <div style={{textAlign : "left", width : "100%"}}>
		{error ? <div className="alert alert-danger" style={{fontSize : "13px"}}>{error}</div> : ""}
		{
			!canWrite ? "" : <div style={{marginBottom : "10px"}}>
				<textarea
				  ref={cajaTexto}
				  className={"form-control" + (faltaTexto ? " is-invalid" : "")}
				  style={{minHeight : "70px"}}
				  maxLength={2000}
				  disabled={ocupado}
				  placeholder={Constants.Common.newInvoiceNotePlaceholder}
				  value={nueva}
				  onChange={(evento : any) => {setNueva(evento.target.value); setFaltaTexto(false)}}
				  onKeyDown={(evento : any) => {if((evento.metaKey || evento.ctrlKey) && evento.key === "Enter"){agregar()}}}
				/>
				{
					!faltaTexto ? "" : <div style={{fontSize : "12px", color : "var(--wa-rojo)", marginTop : "4px"}}>
						{Constants.Common.noteTextRequired}
					</div>
				}
				<div style={{display : "flex", justifyContent : "flex-end", alignItems : "center", marginTop : "6px"}}>
					<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={ocupado} onClick={agregar}>
						<FontAwesomeIcon icon={solid("plus")} />
						&nbsp;
						{Constants.Common.addInvoiceNote}
					</button>
				</div>
			</div>
		}
		{
			notas === null
					?
				<div className="loader" />
					:
				<>
					{
						notas.length < 1
								?
							<div style={{color : "var(--wa-apagado)", fontSize : "13px"}}>{Constants.Common.noInvoiceNotesYet}</div>
								:
							""
					}
					{
						notas.map
						(
							(nota : Nota) => <div key={"nota_" + nota.id} className="card" style=
							{{
								borderLeft   : "4px solid " + (nota.original ? "var(--wa-tinta)" : "var(--wa-acento)"),
								padding      : "8px 12px",
								marginBottom : "8px"
							}}>
								<div style={{fontSize : "12px", color : "var(--wa-apagado)", display : "flex", flexWrap : "wrap", gap : "6px"}}>
									{nota.original ? <b>{Constants.Common.originalInvoiceNote}</b> : ""}
									{nota.original ? <span>·</span> : ""}
									<b>{nota.createdBy || Constants.Common.unknownAuthor}</b>
									<span>·</span>
									<span>{nota.createdAt ? fechaYHora(nota.createdAt) : Constants.Common.unknownDate}</span>
									{nota.edited ? <span>· {Constants.Common.noteEdited}</span> : ""}
								</div>
								{
									enEdicion === nota.id
											?
										<div style={{marginTop : "6px"}}>
											<textarea className="form-control" style={{minHeight : "70px"}} maxLength={2000} disabled={ocupado}
											  value={textoEdicion} onChange={(evento : any) => setTextoEdicion(evento.target.value)}
											/>
											<div style={{display : "flex", gap : "8px", justifyContent : "flex-end", marginTop : "6px"}}>
												<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={ocupado}
												  onClick={() => {setEnEdicion(null); setTextoEdicion("")}}
												>
													{Constants.Common.close}
												</button>
												<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={ocupado}
												  onClick={() => guardarEdicion(nota)}
												>
													{Constants.Common.saveChanges}
												</button>
											</div>
										</div>
											:
										<>
											<div style={{whiteSpace : "pre-wrap", marginTop : "4px"}}>{nota.note}</div>
											{
												!nota.editable ? "" : <div style={{marginTop : "4px"}}>
													<button type="button" className="btn btn-link btn-sm" style={{padding : "0px", fontSize : "12px"}}
													  onClick={() => {setEnEdicion(nota.id); setTextoEdicion(nota.note)}}
													>
														<FontAwesomeIcon icon={solid("pen")} />
														&nbsp;
														{Constants.Common.correctNote}
													</button>
												</div>
											}
										</>
								}
							</div>
						)
					}
				</>
		}
	</div>
}

export default InvoiceNotes
